'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCircleExclamation, faMagnifyingGlass } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import searchAction from '@/app/_lib/actions/SearchAction';
import ProductListingSearchResult from '@/app/_components/ProductListingSearchResult';
import MenuCover from '@/app/_components/MenuCover';
import { useDebouncedValue } from '@/app/_lib/hooks/useDebounce';
import { useAppContext } from '@/app/_lib/_context';
import { trackSearch } from '@/app/_lib/analytics/ga4';
const RESULTS_PER_PAGE = 12;
const NavSearchResults = ({
  activeSearch,
  closeSearch,
  searchButtonRef
}) => {
  const [mobileTab, setMobileTab] = useState('products');
  const [isPending, setIsPending] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNoResultsMessage, setShowNoResultsMessage] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debouncedKeywords = useDebouncedValue(inputValue, 300);
  const [results, setResults] = useState([]);
  const [numFound, setNumFound] = useState(0);
  const [designers, setDesigners] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const inputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const loaderRef = useRef(null);
  const {
    setBodyClass
  } = useAppContext();

  // Lock scroll position while search is active to prevent scroll jumps during typing
  // Only on desktop - mobile needs to allow scroll for keyboard visibility
  useEffect(() => {
    if (!activeSearch) return;

    // Skip scroll lock on mobile (keyboard needs to be able to scroll input into view)
    const isMobile = window.innerWidth < 1024;
    if (isMobile) return;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const preventScroll = () => {
      window.scrollTo(scrollX, scrollY);
    };
    window.addEventListener('scroll', preventScroll);
    return () => {
      window.removeEventListener('scroll', preventScroll);
    };
  }, [activeSearch]);
  useEffect(() => {
    clearTimeout(searchTimeout);
    const isMobile = window.innerWidth < 1024;
    if (activeSearch) {
      setShowSearch(true);
      setShowSearchInput(true);
      // Only lock body scroll on desktop - mobile needs scroll for keyboard
      if (!isMobile) {
        document.body.style.overflow = 'hidden';
      }
      // Focus immediately with minimal delay to ensure DOM is ready
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus({
            preventScroll: !isMobile
          });
          // Restore previous search value if it exists
          if (inputValue) {
            inputRef.current.value = inputValue;
          }
        }
      }, 50);
    } else {
      setShowSearchInput(false);
      document.body.style.overflow = '';
      setSearchTimeout(setTimeout(() => {
        setShowSearch(false);
      }, 700));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearch]);

  /*
   * Keyboard Navigation for Search Results (Accessibility)
   * - Escape: Close search
   * - ArrowDown: Move to next result (or first result from input)
   * - ArrowUp: Move to previous result (or back to input from first result)
   */
  const getResultLinks = useCallback(() => {
    if (!searchContainerRef.current) return [];
    return Array.from(searchContainerRef.current.querySelectorAll('a'));
  }, []);
  const handleKeyDown = useCallback(event => {
    if (event.key === "Escape") {
      closeSearch();
      return;
    }

    // Only handle arrow keys
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const resultLinks = getResultLinks();
    if (resultLinks.length === 0) return;
    const activeElement = document.activeElement;
    const isInInput = activeElement === inputRef.current;
    const currentIndex = resultLinks.indexOf(activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (isInInput) {
        // From input, focus first result
        resultLinks[0]?.focus();
      } else if (currentIndex >= 0 && currentIndex < resultLinks.length - 1) {
        // Move to next result
        resultLinks[currentIndex + 1].focus();
      } else if (currentIndex === resultLinks.length - 1) {
        // Wrap to first result
        resultLinks[0].focus();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (currentIndex === 0) {
        // From first result, go back to input
        inputRef.current?.focus();
      } else if (currentIndex > 0) {
        // Move to previous result
        resultLinks[currentIndex - 1].focus();
      } else if (isInInput) {
        // From input, wrap to last result
        resultLinks[resultLinks.length - 1]?.focus();
      }
    }
  }, [closeSearch, getResultLinks]);
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = event => {
      const isClickOutside = searchContainerRef.current && !searchContainerRef.current.contains(event.target);
      const isClickOnButton = searchButtonRef?.current && searchButtonRef.current.contains(event.target);
      if (isClickOutside && !isClickOnButton && activeSearch) {
        closeSearch();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeSearch, closeSearch, searchButtonRef]);

  // Search effect - triggers on debounced keywords
  useEffect(() => {
    if (debouncedKeywords.length === 0) {
      clearSearchResults();
      setIsPending(false);
      setShowSearchResults(false);
      setShowNoResultsMessage(false);
      return;
    }
    const search = async () => {
      setIsPending(true);
      const response = await searchAction(debouncedKeywords, 0, RESULTS_PER_PAGE);

      // Access unified search response structure
      const productsData = response.payload?.data || [];
      const designers = response.payload?.designers || [];
      const suppliers = response.payload?.suppliers || [];
      const suggestions = response.payload?.suggestions || null;
      const numFound = response.payload?.num_found || 0;
      console.log('Search response:', {
        numFound,
        designers: designers.length,
        suppliers: suppliers.length,
        suggestions
      });
      if (numFound > 0) {
        trackSearch(debouncedKeywords);
        setShowNoResultsMessage(false);
        setShowSearchResults(true);
        setResults(productsData);
        setNumFound(numFound);
        setDesigners(designers);
        setSuppliers(suppliers);
        setSuggestions(suggestions);
        setHasMoreResults(productsData.length < numFound);
      } else {
        clearSearchResults();
        setShowSearchResults(false);
        setShowNoResultsMessage(true);
      }
      setIsPending(false);
    };
    search();
  }, [debouncedKeywords]);

  // Load more results function
  const loadMoreResults = useCallback(async () => {
    if (isLoadingMore || !hasMoreResults || !debouncedKeywords) return;
    setIsLoadingMore(true);
    const response = await searchAction(debouncedKeywords, results.length, RESULTS_PER_PAGE);
    const productsData = response.payload?.data || [];
    if (productsData.length > 0) {
      setResults(prev => [...prev, ...productsData]);
      setHasMoreResults(results.length + productsData.length < numFound);
    } else {
      setHasMoreResults(false);
    }
    setIsLoadingMore(false);
  }, [isLoadingMore, hasMoreResults, debouncedKeywords, results.length, numFound]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!loaderRef.current || !hasMoreResults) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreResults && !isLoadingMore) {
        loadMoreResults();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMoreResults, isLoadingMore, loadMoreResults]);
  const clearSearchResults = () => {
    setNumFound(0);
    setResults([]);
    setDesigners([]);
    setSuppliers([]);
    setSuggestions(null);
    setHasMoreResults(false);
  };
  const handleSuggestionClick = suggestion => {
    setInputValue(suggestion);
    if (inputRef.current) {
      inputRef.current.value = suggestion;
    }
  };
  const handleSearchInput = event => {
    setInputValue(event.target.value);
  };
  if (!activeSearch) {
    return null;
  }
  return <div ref={searchContainerRef} className={`fixed top-[var(--header-height)] group-[.collapsed]/header:top-[var(--header-height-collapsed)] left-0 right-0 w-full z-50 overflow-hidden transition-all duration-300`}>

            <div className={`transition-all duration-700 ease-in-out overflow-hidden ${showSearchInput ? 'opacity-100 h-full' : 'h-0 opacity-0 pointer-events-none'}`}>
                
                <div className={`${results.length > 0 ? 'bg-gray-2' : 'bg-white'} py-3 border-b-1 border-t-1 border-gray-8 w-full`}>
                    <div className="max-w-[1800px] mx-auto site-padding">
                        <div className="flex items-center justify-between w-full text-nowrap">
                            <div className="flex items-center justify-start w-full">
                                <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" className="text-primary text-3xl mr-2 mt-1" />
                                <input ref={inputRef} onChange={handleSearchInput} autoComplete="off" type="text" name="keywords" placeholder="Search" aria-label="Search products" className={`w-full block h-[29px] pt-1 text-[16px] lg:text-md text-light font-medium caret-primary focus:ring-0 focus:outline-none inset-0 bg-transparent`} />
                            </div>
                            <button type="button" onClick={closeSearch} aria-label="Close search" className="hover:text-primary">
                                <FontAwesomeIcon icon={faXmark} aria-hidden="true" className="text-[28px] lg:text-xl" />
                            </button>
                        </div>
                    </div>
                </div>
                
                <div className={`bg-white transition-all duration-700 ease-in-out ${!showNoResultsMessage ? 'h-0 hidden opacity-0 pointer-events-none' : 'opacity-100 h-full site-padding py-4'}`}>
                    <div className="flex items-center justify-start md:justify-center relative w-full bg-white text-nowrap">
                        <FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" className="text-primary text-md md:text-xl mr-2" />
                        <span className="text-light text-sm"><span className="font-medium">Sorry!</span> We couldn't find any search results for &quot;{inputValue}&quot;</span>
                    </div>
                </div>
                {
        /*
        search results
        */
      }
                {results.length > 0 && <div className={`bg-white text-left transition-all duration-700 ease-in-out flex flex-col h-[calc(100vh-var(--header-height)-50px)] lg:h-[70vh] ${!showSearchResults ? 'h-0 opacity-0 pointer-events-none' : 'opacity-100'}`}>
                        <div aria-live="polite" aria-atomic="true" className="sr-only">
                            {isPending ? 'Searching...' : numFound > 0 ? `${numFound} results found` : showNoResultsMessage ? 'No results found' : ''}
                        </div>
                        {/* Mobile tabs - products, brands, designers */}
                        <div className="lg:hidden flex gap-6 site-padding py-3 border-b border-gray-8">
                            <button type="button" onClick={() => setMobileTab('products')} className={`text-xs font-medium transition-colors ${mobileTab === 'products' ? 'text-primary' : 'text-light hover:text-dark'}`}>
                                Products {numFound > 0 && <span className="text-light-3">({numFound})</span>}
                            </button>
                            <button type="button" onClick={() => setMobileTab('brands')} className={`text-xs font-medium transition-colors ${mobileTab === 'brands' ? 'text-primary' : 'text-light hover:text-dark'}`}>
                                Brands {suppliers.length > 0 && <span className="text-light-3">({suppliers.length})</span>}
                            </button>
                            <button type="button" onClick={() => setMobileTab('designers')} className={`text-xs font-medium transition-colors ${mobileTab === 'designers' ? 'text-primary' : 'text-light hover:text-dark'}`}>
                                Designers {designers.length > 0 && <span className="text-light-3">({designers.length})</span>}
                            </button>
                        </div>

                        <div className="flex-1 flex flex-col lg:flex-row site-padding pt-4 lg:pt-4 overflow-y-auto pb-4 lg:pb-4">
                        {/* Brands column */}
                        <div className={`${mobileTab === 'brands' ? 'block' : 'hidden'} lg:block lg:w-[10%] lg:min-w-[140px] lg:shrink-0 pb-2 lg:pb-0`}>
                            <div className={`hidden lg:block font-medium text-xs mb-4 ${suppliers.length === 0 ? 'text-light-3' : 'text-light'}`}>
                                Brands
                            </div>
                            <div className="grid grid-cols-2 gap-4 lg:block">
                                {suppliers.length > 0 && <>
                                        {suppliers.filter(s => s.url).map((supplier, index) => <Link key={supplier.supplier_id || index} href={supplier.url} onClick={() => closeSearch()} className="flex flex-col items-start hover:opacity-80 lg:mb-6 transition-colors duration-200">
                                                {supplier.image && <span className="overlay-block">
                                                        <img src={supplier.image} alt={supplier.supplier_name} className="w-full lg:w-32 aspect-[8/7] object-cover" />
                                                    </span>}
                                                <span className="text-2xs text-light mt-1">{supplier.supplier_name} {supplier.product_count > 0 && <span className="text-light-3">({supplier.product_count})</span>}</span>
                                            </Link>)}
                                    </>}
                            </div>
                        </div>

                        {/* Designers column */}
                        <div className={`${mobileTab === 'designers' ? 'block' : 'hidden'} lg:block lg:w-[10%] lg:min-w-[140px] lg:shrink-0`}>
                            <div className={`hidden lg:block font-medium text-xs mb-4 ${designers.length === 0 ? 'text-light-3' : 'text-light'}`}>
                                Designers
                            </div>
                            <div className="grid grid-cols-2 gap-4 lg:block">
                                {designers.length > 0 && <>
                                        {designers.filter(d => d.url).map((designer, index) => <Link key={designer.designer_id || index} href={designer.url} onClick={() => closeSearch()} className="flex flex-col items-start hover:opacity-80 lg:mb-6 transition-colors duration-200">
                                                {designer.image && <span className="overlay-block">
                                                        <img src={designer.image} alt={designer.designer_name} className="w-full lg:w-32 h-auto object-contain" />
                                                    </span>}
                                                <span className="text-2xs text-light mt-1">{designer.designer_name} {designer.product_count > 0 && <span className="text-light-3">({designer.product_count})</span>}</span>
                                            </Link>)}
                                    </>}
                            </div>
                        </div>

                        {/* Products column - takes remaining space */}
                        <div className={`${mobileTab === 'products' ? 'block' : 'hidden'} lg:block lg:flex-1 lg:ml-[5%]`}>
                            <div className="hidden lg:flex font-medium text-xs mb-4 items-baseline gap-2">
                                <span>Products</span>
                                {numFound > 0 && <span className="text-light font-normal"><span className="text-light-3">({numFound})</span> results for <span className="underline text-primary">{inputValue}</span></span>}
                            </div>

                            {numFound > 0 ? <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 pb-4">
                                        {results.filter(product => product.image).map((product, index) => <div key={`${product.sub_product_id}-${index}`} className="border-b md:border-b-0 border-gray-8 mb-6" onClick={() => closeSearch()}>
                                                <ProductListingSearchResult product={product} additionalClasses={''} closeSearch={closeSearch} />
                                            </div>)}
                                    </div>
                                    {/* Infinite scroll loader */}
                                    {hasMoreResults && <div ref={loaderRef} className="flex flex-col items-center justify-center pt-6 pb-12 text-light">
                                            {isLoadingMore ? <div className="flex items-center gap-2">
                                                    <FontAwesomeIcon icon={faSpinnerThird} aria-hidden="true" className="animate-spin text-primary text-lg" />
                                                    <span className="text-sm">Loading more products...</span>
                                                </div> : <span className="text-xs text-light-3">Scroll for more results</span>}
                                        </div>}
                                    {!hasMoreResults && results.length > 0 && results.length >= numFound && <div className="text-center py-4 text-xs text-light-3">
                                            Showing all {numFound} results
                                        </div>}
                                </> : <div className="text-base font-extra-light text-light col-span-6 mt-12">
                                    {isPending && <div className="flex items-center gap-2">
                                            <FontAwesomeIcon icon={faSpinnerThird} aria-hidden="true" className="animate-spin text-primary text-lg" />
                                            <span className="text-sm">Searching...</span>
                                        </div>}
                                </div>}
                        </div>
                        </div>
                        {/* Fixed bottom bar outside scroll container - only show if 9+ results */}
                        {numFound >= 9 && <div className="shrink-0 bg-white border-t border-gray-8 site-padding py-6">
                                <Link href={`/search?keywords=${inputValue}`} onClick={() => closeSearch()} className="btn btn-sm btn-extra-light flex items-center justify-center gap-2 max-w-md mx-auto">
                                    <FontAwesomeIcon icon={faHiveIconSmallLight} className="text-primary text-lg" />
                                    <span>View All Results for &quot;{inputValue}&quot; ({numFound})</span>
                                </Link>
                            </div>}
                    </div>}
            </div>
        </div>;
};
const NavSearchWidget = () => {
  const $ = _c(19);
  const {
    activeSearch,
    openSearch,
    closeSearch,
    closeMobileMenu
  } = useAppContext();
  const searchButtonRef = useRef(null);
  let t0;
  if ($[0] !== activeSearch || $[1] !== closeMobileMenu || $[2] !== closeSearch || $[3] !== openSearch) {
    t0 = event => {
      event.preventDefault();
      closeMobileMenu();
      if (activeSearch) {
        closeSearch();
      } else {
        openSearch();
      }
    };
    $[0] = activeSearch;
    $[1] = closeMobileMenu;
    $[2] = closeSearch;
    $[3] = openSearch;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const handleSearchClick = t0;
  let t1;
  if ($[5] !== activeSearch || $[6] !== closeSearch) {
    t1 = <MenuCover close={closeSearch} coverVisible={activeSearch} />;
    $[5] = activeSearch;
    $[6] = closeSearch;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" className="group-hover:text-primary text-[1.4rem] lg:text-nav-icon group-[.transparent]/header:text-white" />;
    t3 = <span className="hidden xl:block text-md ml-1 group-hover:text-primary group-[.transparent]/header:text-white">Search</span>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  let t4;
  if ($[10] !== handleSearchClick) {
    t4 = <button ref={searchButtonRef} onClick={handleSearchClick} className="flex items-center group" aria-label="Search">{t2}{t3}</button>;
    $[10] = handleSearchClick;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== activeSearch || $[13] !== closeSearch) {
    t5 = <NavSearchResults activeSearch={activeSearch} closeSearch={closeSearch} searchButtonRef={searchButtonRef} />;
    $[12] = activeSearch;
    $[13] = closeSearch;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t1 || $[16] !== t4 || $[17] !== t5) {
    t6 = <div>{t1}{t4}{t5}</div>;
    $[15] = t1;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export default NavSearchWidget;